import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Copy, Download, ExternalLink, Globe, Loader2, RefreshCw, Share2 } from 'lucide-react';
import html2canvas from 'html2canvas';
import { GuestGalleryMasonryGrid } from '@/components/planner/GuestGalleryMasonryGrid';
import { GuestGalleryLightbox } from '@/components/planner/GuestGalleryLightbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

if (typeof document !== 'undefined' && !document.getElementById('guest-gallery-poster-fonts')) {
  const fontPreconnectOne = document.createElement('link');
  fontPreconnectOne.rel = 'preconnect';
  fontPreconnectOne.href = 'https://fonts.googleapis.com';
  fontPreconnectOne.id = 'guest-gallery-poster-fonts-preconnect-one';
  document.head.appendChild(fontPreconnectOne);

  const fontPreconnectTwo = document.createElement('link');
  fontPreconnectTwo.rel = 'preconnect';
  fontPreconnectTwo.href = 'https://fonts.gstatic.com';
  fontPreconnectTwo.crossOrigin = 'anonymous';
  fontPreconnectTwo.id = 'guest-gallery-poster-fonts-preconnect-two';
  document.head.appendChild(fontPreconnectTwo);

  const fontStylesheet = document.createElement('link');
  fontStylesheet.rel = 'stylesheet';
  fontStylesheet.href = 'https://fonts.googleapis.com/css2?family=Allura&family=Great+Vibes&family=Montserrat:wght@400;500;600;700&family=Parisienne&display=swap';
  fontStylesheet.id = 'guest-gallery-poster-fonts';
  document.head.appendChild(fontStylesheet);
}

const DEFAULT_POSTER_STYLE = 'modern-script';

export const GuestGallerySection = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [savingVisibility, setSavingVisibility] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [galleryData, setGalleryData] = useState(null);
  const [metaDraft, setMetaDraft] = useState({ couple_names: '', wedding_date: '' });
  const [metaDirty, setMetaDirty] = useState(false);
  const [savingMeta, setSavingMeta] = useState(false);
  const [posterStyle, setPosterStyle] = useState(DEFAULT_POSTER_STYLE);
  const [downloadingPoster, setDownloadingPoster] = useState(false);

  const loadGallery = useCallback(async ({ silent = false } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await axios.get(`${API}/planner/guest-gallery`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGalleryData(response.data || null);
    } catch (error) {
      toast.error('Could not load your guest photo gallery yet.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    loadGallery();
    const timer = window.setInterval(() => {
      loadGallery({ silent: true });
    }, 20000);
    return () => window.clearInterval(timer);
  }, [loadGallery]);

  useEffect(() => {
    if (!galleryData) return;
    if (metaDirty || savingMeta) return;
    setMetaDraft({
      couple_names: galleryData.couple_names || '',
      wedding_date: galleryData.wedding_date || '',
    });
    setMetaDirty(false);
  }, [galleryData?.couple_names, galleryData?.wedding_date, galleryData?.id, metaDirty, savingMeta]);

  useEffect(() => {
    if (!galleryData) return;
    if (metaDirty || savingMeta) return;
    setPosterStyle(galleryData.poster_style || DEFAULT_POSTER_STYLE);
  }, [galleryData?.poster_style, galleryData?.id, metaDirty, savingMeta]);

  const uploadUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    if (!galleryData?.slug) return '';
    return `${window.location.origin}/guest-gallery/${galleryData.slug}/upload`;
  }, [galleryData]);

  const formattedPosterDate = useMemo(() => {
    const raw = (metaDraft.wedding_date || '').trim();
    if (!raw) return '';
    const [year, month, day] = raw.split('-');
    if (!year || !month || !day) return raw;
    return `${day}.${month}.${year}`;
  }, [metaDraft.wedding_date]);

  const scriptFontOptions = useMemo(() => {
    return [
      { id: 'modern-script', label: 'Script A · Modern', fontFamily: '"Great Vibes", cursive' },
      { id: 'elegant-script', label: 'Script B · Elegant', fontFamily: '"Allura", cursive' },
      { id: 'handwritten-script', label: 'Script C · Handwritten', fontFamily: '"Parisienne", cursive' },
    ];
  }, []);

  const activeScriptFont = useMemo(() => {
    return scriptFontOptions.find((option) => option.id === posterStyle) || scriptFontOptions[0];
  }, [posterStyle, scriptFontOptions]);

  const publicGalleryUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    if (!galleryData?.public_gallery_path) return '';
    return `${window.location.origin}${galleryData.public_gallery_path}`;
  }, [galleryData]);

  const photos = useMemo(
    () => (Array.isArray(galleryData?.photos) ? galleryData.photos : []),
    [galleryData],
  );

  useEffect(() => {
    if (lightboxIndex === null) return;
    if (lightboxIndex < photos.length) return;
    setLightboxIndex(null);
  }, [lightboxIndex, photos.length]);

  const copyText = async (textToCopy, successMessage) => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      toast.success(successMessage);
    } catch {
      toast.error('Could not copy link right now');
    }
  };

  const copyUploadLink = async () => {
    if (!uploadUrl) return;
    await copyText(uploadUrl, 'Upload link copied');
  };

  const shareUploadLink = async () => {
    if (!uploadUrl) return;
    const shareText = `Upload your wedding photos here: ${uploadUrl}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Wedding Guest Photo Upload',
          text: 'Upload your photos from the wedding',
          url: uploadUrl,
        });
        return;
      } catch {
        // fall through to WhatsApp share URL
      }
    }

    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank', 'noopener,noreferrer');
  };

  const downloadPosterImage = async () => {
    if (!uploadUrl || downloadingPoster) return;

    const posterNode = document.getElementById('planner-guest-gallery-print-poster');
    if (!posterNode) {
      toast.error('Could not prepare poster image.');
      return;
    }

    setDownloadingPoster(true);
    try {
      const canvas = await html2canvas(posterNode, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
      });

      const imageUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      const safeName = (metaDraft.couple_names || 'guest-gallery').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      link.href = imageUrl;
      link.download = `${safeName || 'guest-gallery'}-qr-poster.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Poster image downloaded');
    } catch (error) {
      toast.error('Could not download poster image right now.');
    } finally {
      setDownloadingPoster(false);
    }
  };

  const printPoster = () => {
    const posterNode = document.getElementById('planner-guest-gallery-print-poster');
    if (!posterNode) {
      toast.error('Could not prepare poster for print.');
      return;
    }

    const printWindow = window.open('', '_blank', 'noopener,noreferrer,width=900,height=1200');
    if (!printWindow) {
      toast.error('Please allow pop-ups to print poster.');
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>Guest Gallery QR Poster</title>
          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
          <link href="https://fonts.googleapis.com/css2?family=Allura&family=Great+Vibes&family=Montserrat:wght@400;500;600;700&family=Parisienne&display=swap" rel="stylesheet" />
          <style>
            body { margin: 0; padding: 20px; background: #f8f6f2; font-family: 'Montserrat', sans-serif; }
            .poster-wrap { display: flex; justify-content: center; }
          </style>
        </head>
        <body>
          <div class="poster-wrap">${posterNode.outerHTML}</div>
          <script>
            window.onload = function () {
              setTimeout(function(){ window.print(); }, 300);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const copyPublicGalleryLink = async () => {
    if (!publicGalleryUrl) return;
    await copyText(publicGalleryUrl, 'Public gallery link copied');
  };

  const setPublicGalleryVisibility = async (nextPublicValue) => {
    if (!galleryData?.public_gallery_path) {
      toast.error('Publish your wedding website first to enable a public gallery link.');
      return;
    }

    if (savingVisibility) return;

    setSavingVisibility(true);
    try {
      await axios.put(
        `${API}/planner/guest-gallery/visibility`,
        { is_public: Boolean(nextPublicValue) },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setGalleryData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          is_public: Boolean(nextPublicValue),
        };
      });

      toast.success(nextPublicValue ? 'Public gallery is now visible to guests.' : 'Public gallery has been hidden.');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not update public gallery visibility.');
    } finally {
      setSavingVisibility(false);
    }
  };

  const downloadAllPhotosZip = async () => {
    if (downloadingZip || photos.length === 0) return;

    setDownloadingZip(true);
    try {
      const response = await axios.get(`${API}/planner/guest-gallery/download-zip`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const disposition = response.headers['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const filename = filenameMatch?.[1] || 'guest-photos.zip';

      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Download started');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not download ZIP right now');
    } finally {
      setDownloadingZip(false);
    }
  };

  useEffect(() => {
    if (!metaDirty) return undefined;
    if (!metaDraft.couple_names?.trim()) return undefined;

    const timer = window.setTimeout(async () => {
      if (savingMeta) return;
      setSavingMeta(true);
      try {
        const payload = {
          couple_names: metaDraft.couple_names.trim(),
          wedding_date: metaDraft.wedding_date || '',
          poster_style: posterStyle || DEFAULT_POSTER_STYLE,
        };

        const response = await axios.put(
          `${API}/planner/guest-gallery/meta`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        setGalleryData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            couple_names: response.data?.couple_names || payload.couple_names,
            wedding_date: response.data?.wedding_date || payload.wedding_date,
            poster_style: response.data?.poster_style || payload.poster_style,
          };
        });

        setMetaDirty(false);
      } catch (error) {
        toast.error(error?.response?.data?.detail || 'Could not save gallery title/date');
      } finally {
        setSavingMeta(false);
      }
    }, 800);

    return () => window.clearTimeout(timer);
  }, [metaDraft.couple_names, metaDraft.wedding_date, metaDirty, posterStyle, savingMeta, token]);

  if (loading) {
    return (
      <div className="rounded-sm border border-stone-200 bg-white p-8 text-center" data-testid="planner-guest-gallery-loading">
        <Loader2 className="h-6 w-6 animate-spin text-stone-400 mx-auto" />
      </div>
    );
  }

  if (!galleryData) {
    return (
      <div className="rounded-sm border border-stone-200 bg-white p-8 text-center" data-testid="planner-guest-gallery-error-state">
        <p className="text-stone-600">Unable to load your guest gallery. Please try again.</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => loadGallery()}
          data-testid="planner-guest-gallery-retry-button"
        >
          Try again
        </Button>
      </div>
    );
  }

  const hasPhotos = photos.length > 0;
  const publicGalleryEnabled = Boolean(galleryData?.public_gallery_path) && Boolean(galleryData?.is_public);

  return (
    <div className="space-y-6" data-testid="planner-guest-gallery-section">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-stone-900" data-testid="planner-guest-gallery-title">📸 Guest Photo Sharing</h2>
          <p className="text-sm text-stone-600 mt-1" data-testid="planner-guest-gallery-subtitle">
            Let your guests upload photos from your wedding day
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={downloadAllPhotosZip}
            disabled={photos.length === 0 || downloadingZip}
            className="border-stone-300"
            data-testid="planner-guest-gallery-download-zip-button"
          >
            {downloadingZip ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
            Download all photos (ZIP)
          </Button>
          <Button
            variant="outline"
            onClick={() => loadGallery({ silent: true })}
            className="border-stone-300"
            data-testid="planner-guest-gallery-refresh-button"
          >
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
        </div>
      </div>

      <div className="rounded-sm border border-stone-200 bg-white p-5" data-testid="planner-guest-gallery-share-panel">
        <div className="mb-5 rounded-sm border border-stone-200 bg-stone-50 p-4" data-testid="planner-guest-gallery-meta-editor-card">
          <div className="mb-3 flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-stone-900" data-testid="planner-guest-gallery-meta-editor-title">
              Guest gallery title &amp; wedding date
            </p>
            {savingMeta ? (
              <span className="inline-flex items-center text-xs text-stone-500" data-testid="planner-guest-gallery-meta-saving-state">
                <Loader2 className="mr-1 h-3 w-3 animate-spin" /> Saving…
              </span>
            ) : (
              <span className="text-xs text-stone-500" data-testid="planner-guest-gallery-meta-autosave-note">Auto-saves</span>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="planner-guest-gallery-meta-fields-grid">
            <label className="space-y-1" data-testid="planner-guest-gallery-couple-name-field-wrap">
              <span className="text-xs font-medium text-stone-700" data-testid="planner-guest-gallery-couple-name-label">Couple title</span>
              <input
                value={metaDraft.couple_names}
                onChange={(event) => {
                  setMetaDraft((prev) => ({ ...prev, couple_names: event.target.value }));
                  setMetaDirty(true);
                }}
                placeholder="Marian & Thomas"
                className="h-10 w-full rounded-sm border border-stone-300 px-3 text-sm outline-none focus:border-[#5C0120] focus:ring-1 focus:ring-[#5C0120]"
                data-testid="planner-guest-gallery-couple-name-input"
              />
            </label>

            <label className="space-y-1" data-testid="planner-guest-gallery-wedding-date-field-wrap">
              <span className="text-xs font-medium text-stone-700" data-testid="planner-guest-gallery-wedding-date-label">Wedding date</span>
              <input
                type="date"
                value={metaDraft.wedding_date || ''}
                onChange={(event) => {
                  setMetaDraft((prev) => ({ ...prev, wedding_date: event.target.value }));
                  setMetaDirty(true);
                }}
                className="h-10 w-full rounded-sm border border-stone-300 px-3 text-sm outline-none focus:border-[#5C0120] focus:ring-1 focus:ring-[#5C0120]"
                data-testid="planner-guest-gallery-wedding-date-input"
              />
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[220px,1fr] items-start">
          <div className="flex justify-center md:justify-start">
            {uploadUrl ? (
              <QRCodeSVG value={uploadUrl} size={200} bgColor="#ffffff" fgColor="#1c1917" includeMargin data-testid="planner-guest-gallery-qr-code" />
            ) : (
              <div className="h-[200px] w-[200px] border border-stone-200 bg-stone-50" data-testid="planner-guest-gallery-qr-placeholder" />
            )}
          </div>
          <div className="space-y-4">
            <p className="text-sm text-stone-600" data-testid="planner-guest-gallery-instructions">
              Print this QR code or share the link with your guests. They can upload photos during your wedding — no login needed.
            </p>
            <div className="flex flex-wrap gap-2" data-testid="planner-guest-gallery-actions">
              <Button onClick={copyUploadLink} className="bg-[#5C0120] hover:bg-[#5C0120]/90" data-testid="planner-guest-gallery-copy-link-button">
                <Copy className="h-4 w-4 mr-2" />
                Copy link
              </Button>
              <Button variant="outline" onClick={shareUploadLink} className="border-[#5C0120] text-[#5C0120] hover:bg-[#5C0120]/10" data-testid="planner-guest-gallery-share-button">
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
              <a href={uploadUrl} target="_blank" rel="noopener noreferrer" data-testid="planner-guest-gallery-open-upload-link">
                <Button variant="outline" className="border-stone-300 text-stone-700">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open upload page
                </Button>
              </a>
            </div>

            <div className="rounded-sm border border-stone-200 bg-stone-50 p-4" data-testid="planner-guest-gallery-poster-tools-card">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-stone-900" data-testid="planner-guest-gallery-poster-tools-title">
                  Download & print QR poster
                </p>
                <span className="text-xs text-stone-500" data-testid="planner-guest-gallery-poster-tools-hint">
                  Couple name + date update automatically
                </span>
              </div>

              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="planner-guest-gallery-poster-font-options-wrap">
                <label className="space-y-1" data-testid="planner-guest-gallery-poster-font-label-wrap">
                  <span className="text-xs font-medium text-stone-700" data-testid="planner-guest-gallery-poster-font-label">Script font style</span>
                  <select
                    value={posterStyle}
                    onChange={(event) => {
                      setPosterStyle(event.target.value);
                      setMetaDirty(true);
                    }}
                    className="h-10 w-full rounded-sm border border-stone-300 px-3 text-sm outline-none focus:border-[#5C0120] focus:ring-1 focus:ring-[#5C0120]"
                    data-testid="planner-guest-gallery-poster-font-select"
                  >
                    {scriptFontOptions.map((option) => (
                      <option key={option.id} value={option.id}>{option.label}</option>
                    ))}
                  </select>
                </label>

                <div className="flex flex-wrap items-end gap-2" data-testid="planner-guest-gallery-poster-action-buttons-wrap">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={downloadPosterImage}
                    disabled={downloadingPoster || !uploadUrl}
                    className="border-[#5C0120] text-[#5C0120] hover:bg-[#5C0120]/10"
                    data-testid="planner-guest-gallery-download-poster-image-button"
                  >
                    {downloadingPoster ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
                    Download image (PNG)
                  </Button>
                  <Button
                    type="button"
                    onClick={printPoster}
                    className="bg-[#5C0120] hover:bg-[#5C0120]/90"
                    data-testid="planner-guest-gallery-print-poster-button"
                  >
                    Print poster
                  </Button>
                </div>
              </div>

              <div className="mt-4 flex justify-center" data-testid="planner-guest-gallery-poster-preview-wrap">
                <div
                  id="planner-guest-gallery-print-poster"
                  className="w-[320px] max-w-full rounded-sm border border-stone-200 bg-white p-6 text-center shadow-sm"
                  data-testid="planner-guest-gallery-poster-preview"
                >
                  <p className="text-[13px] tracking-[0.35em] text-stone-700" data-testid="planner-guest-gallery-poster-preview-share-kicker">SHARE</p>
                  <p
                    className="mt-2 text-[64px] leading-none text-stone-900"
                    style={{ fontFamily: activeScriptFont.fontFamily }}
                    data-testid="planner-guest-gallery-poster-preview-script-title"
                  >
                    the love
                  </p>
                  <p className="mt-4 text-[13px] font-medium tracking-[0.25em] text-stone-700" data-testid="planner-guest-gallery-poster-preview-subtitle-line-1">
                    SHARE YOUR PHOTOS WITH US!
                  </p>
                  <p className="mt-1 text-[13px] font-medium tracking-[0.25em] text-stone-700" data-testid="planner-guest-gallery-poster-preview-subtitle-line-2">
                    SIMPLY SCAN AND UPLOAD
                  </p>

                  <div className="mt-5 flex justify-center" data-testid="planner-guest-gallery-poster-preview-qr-wrap">
                    {uploadUrl ? (
                      <QRCodeSVG value={uploadUrl} size={180} bgColor="#ffffff" fgColor="#111111" includeMargin data-testid="planner-guest-gallery-poster-preview-qr" />
                    ) : (
                      <div className="h-[180px] w-[180px] border border-stone-200 bg-stone-50" />
                    )}
                  </div>

                  <p
                    className="mt-5 text-[54px] leading-none text-stone-900"
                    style={{ fontFamily: activeScriptFont.fontFamily }}
                    data-testid="planner-guest-gallery-poster-preview-couple-name"
                  >
                    {metaDraft.couple_names || 'Your Names'}
                  </p>
                  <p className="mt-3 text-[12px] tracking-[0.3em] text-stone-700" data-testid="planner-guest-gallery-poster-preview-date">
                    {formattedPosterDate || 'DD.MM.YYYY'}
                  </p>
                </div>
              </div>
            </div>

            <p className="text-xs text-stone-500 break-all" data-testid="planner-guest-gallery-upload-url">
              {uploadUrl}
            </p>

            <div className="rounded-sm border border-stone-200 bg-stone-50 p-4" data-testid="planner-guest-gallery-public-visibility-card">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-medium text-stone-900" data-testid="planner-guest-gallery-public-visibility-title">Public Gallery Access</p>
                  <p className="mt-1 text-xs text-stone-600" data-testid="planner-guest-gallery-public-visibility-description">
                    Allow anyone with the wedding gallery link to view shared photos.
                  </p>
                </div>
                <div className="flex items-center gap-2" data-testid="planner-guest-gallery-public-visibility-toggle-wrap">
                  {savingVisibility && <Loader2 className="h-4 w-4 animate-spin text-stone-500" data-testid="planner-guest-gallery-public-visibility-saving" />}
                  <Switch
                    checked={Boolean(galleryData?.is_public)}
                    onCheckedChange={setPublicGalleryVisibility}
                    disabled={savingVisibility || !galleryData?.public_gallery_path}
                    data-testid="planner-guest-gallery-public-visibility-toggle"
                    aria-label="Toggle public guest gallery visibility"
                  />
                </div>
              </div>

              {!galleryData?.public_gallery_path ? (
                <p className="mt-3 text-xs text-amber-700" data-testid="planner-guest-gallery-public-visibility-hint-publish-first">
                  Publish your wedding website first to unlock the public gallery URL.
                </p>
              ) : (
                <>
                  <p className="mt-3 break-all text-xs text-stone-500" data-testid="planner-guest-gallery-public-url">
                    {publicGalleryUrl}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="planner-guest-gallery-public-actions">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={copyPublicGalleryLink}
                      disabled={!publicGalleryEnabled}
                      className="border-stone-300"
                      data-testid="planner-guest-gallery-copy-public-link-button"
                    >
                      <Copy className="mr-2 h-4 w-4" />
                      Copy public link
                    </Button>

                    <a
                      href={publicGalleryEnabled ? publicGalleryUrl : undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="planner-guest-gallery-open-public-link"
                    >
                      <Button
                        type="button"
                        variant="outline"
                        disabled={!publicGalleryEnabled}
                        className="border-stone-300"
                        data-testid="planner-guest-gallery-open-public-link-button"
                      >
                        <Globe className="mr-2 h-4 w-4" />
                        Open public gallery
                      </Button>
                    </a>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {hasPhotos ? (
        <section data-testid="planner-guest-gallery-grid-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-stone-900" data-testid="planner-guest-gallery-grid-title">
              Shared Photos ({photos.length})
            </h3>
            <p className="text-xs text-stone-500" data-testid="planner-guest-gallery-grid-caption">
              Tap any photo to open full screen
            </p>
          </div>
          <GuestGalleryMasonryGrid
            photos={photos}
            onPhotoClick={setLightboxIndex}
            testIdPrefix="planner-guest-gallery-masonry"
          />
        </section>
      ) : (
        <div className="rounded-sm border border-stone-200 bg-white p-10 text-center" data-testid="planner-guest-gallery-empty-state">
          <p className="text-stone-700 font-medium">No photos yet</p>
          <p className="text-sm text-stone-500 mt-1">Once guests upload photos, your gallery will appear here automatically.</p>
        </div>
      )}

      <GuestGalleryLightbox
        isOpen={lightboxIndex !== null}
        photos={photos}
        currentIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onNext={() => setLightboxIndex((prev) => (prev === null ? 0 : (prev + 1) % photos.length))}
        onPrev={() => setLightboxIndex((prev) => (prev === null ? 0 : (prev - 1 + photos.length) % photos.length))}
        testIdPrefix="planner-guest-gallery-lightbox"
      />
    </div>
  );
};
